/**
 * Copyright 2017-2021 Sourcepole AG
 * All rights reserved.
 *
 * This source code is licensed under the BSD-style license found in the
 * LICENSE file in the root directory of this source tree.
 *
 * Fork of qwc2/components/ResizeableWindow (React 19 / new upstream) with:
 *  - accordion sidebar docking (docked windows stack in a left sidebar, one maximized, others collapsed)
 *  - feature highlighting
 *  - disabled extra controls
 * The map area is shrunk via upstream's split-screen mechanism (state.windows.mapMargins).
 */

import React from 'react';
import {connect} from 'react-redux';

import classnames from 'classnames';
import PropTypes from 'prop-types';
import {Rnd} from 'react-rnd';
import {v4 as uuidv4} from 'uuid';

import {addLayerFeatures, LayerRole, removeLayer} from 'qwc2/actions/layers';
import {raiseWindow, registerWindow, setSplitScreen, unregisterWindow} from 'qwc2/actions/windows';
import Icon from 'qwc2/components/Icon';
import ConfigUtils from 'qwc2/utils/ConfigUtils';
import LocaleUtils from 'qwc2/utils/LocaleUtils';

import {unregisterSideWindow, registerSideWindow, setSidebarWidth, sidebarMaximize} from '../../actions/sidebar';

import 'qwc2/components/style/ResizeableWindow.css';
import './style/CustomResizeableWindow.css';

const MINIMIZED_HEIGHT = 35;
const GAP = 2;

class CustomResizeableWindow extends React.Component {
    static propTypes = {
        addLayerFeatures: PropTypes.func,
        baseZIndex: PropTypes.number,
        bottombarHeight: PropTypes.number,
        children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
        dockable: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
        extraControls: PropTypes.arrayOf(PropTypes.shape({
            icon: PropTypes.string.isRequired,
            callback: PropTypes.func.isRequired,
            disabled: PropTypes.bool,
            title: PropTypes.string,
            titlemsgid: PropTypes.string
        })),
        feature: PropTypes.object,
        icon: PropTypes.string,
        initialHeight: PropTypes.number,
        initialWidth: PropTypes.number,
        initialX: PropTypes.number,
        initialY: PropTypes.number,
        initiallyDocked: PropTypes.bool,
        maxHeight: PropTypes.number,
        maxWidth: PropTypes.number,
        maximizeable: PropTypes.bool,
        menuMargins: PropTypes.object,
        minHeight: PropTypes.number,
        minWidth: PropTypes.number,
        minimizeable: PropTypes.bool,
        onClose: PropTypes.func,
        onGeometryChanged: PropTypes.func,
        raiseWindow: PropTypes.func,
        registerSideWindow: PropTypes.func,
        registerWindow: PropTypes.func,
        removeLayer: PropTypes.func,
        scrollable: PropTypes.bool,
        setSidebarWidth: PropTypes.func,
        setSplitScreen: PropTypes.func,
        sidebarMaximize: PropTypes.func,
        sidebarMaximized: PropTypes.string,
        sidebarWidth: PropTypes.number,
        sidewindows: PropTypes.array,
        title: PropTypes.string,
        titlelabel: PropTypes.string,
        topbarHeight: PropTypes.number,
        unregisterSideWindow: PropTypes.func,
        unregisterWindow: PropTypes.func,
        visible: PropTypes.bool,
        windowStacking: PropTypes.array
    };
    static defaultProps = {
        baseZIndex: 10,
        initialX: null,
        initialY: null,
        initialWidth: 240,
        initialHeight: 320,
        minWidth: 50,
        minHeight: 50,
        maxWidth: null,
        maxHeight: null,
        maximizeable: true,
        minimizeable: false,
        visible: true,
        dockable: true,
        onGeometryChanged: () => {}
    };
    state = {
        geometry: null
    };

    constructor(props) {
        super(props);
        this.rnd = null;
        this.dragShield = null;
        this.id = uuidv4();
        this.splitKey = null;
        const height = Math.min(props.initialHeight, window.innerHeight - 100);
        const width = Math.min(props.initialWidth, window.innerWidth);
        this.state.geometry = {
            x: props.initialX !== null ? this.computeInitialX(props.initialX) : Math.max(0, Math.round(0.5 * (window.innerWidth - width))),
            y: props.initialY !== null ? this.computeInitialY(props.initialY) : Math.max(0, Math.round(0.5 * (window.innerHeight - height))),
            width: width,
            height: height,
            docked: false
        };
    }
    computeInitialX = (x) => {
        return x >= 0 ? x : window.innerWidth - Math.abs(x);
    };
    computeInitialY = (y) => {
        return y >= 0 ? y : window.innerHeight - Math.abs(y);
    };
    isDockable = () => {
        return !!this.props.dockable && !ConfigUtils.getConfigProp("globallyDisableDockableDialogs");
    };
    componentDidMount() {
        this.props.registerWindow(this.id);
        this.highlight(this.props.feature);
        if (this.props.initiallyDocked && this.isDockable()) {
            this.props.registerSideWindow(this.id);
            this.props.sidebarMaximize(this.id);
            this.setState((state) => ({geometry: {...state.geometry, docked: true}}));
        }
    }
    componentWillUnmount() {
        this.props.unregisterWindow(this.id);
        this.props.unregisterSideWindow(this.id);
        this.props.setSplitScreen(this.id, null, null, false);
    }
    componentDidUpdate(prevProps, prevState) {
        const geometry = this.state.geometry;
        if (this.rnd && this.props.visible && this.props.visible !== prevProps.visible) {
            this.rnd.updatePosition(geometry);
        }
        if (geometry !== prevState.geometry) {
            this.props.onGeometryChanged(geometry);
        }
        if (this.props.feature !== prevProps.feature) {
            if (!this.props.feature && prevProps.feature) {
                this.props.removeLayer("identifyslection");
            }
            this.highlight(this.props.feature);
        }
        // A hidden docked window must not occupy a slot in the accordion
        if (geometry.docked && this.props.visible !== prevProps.visible) {
            if (this.props.visible) {
                this.props.registerSideWindow(this.id);
            } else {
                this.props.unregisterSideWindow(this.id);
            }
        }
        this.syncSplitScreen();
    }
    syncSplitScreen = () => {
        // Shrink the map area to the right of the sidebar via upstream's split screen mechanism
        const geometry = this.state.geometry;
        const inSidebar = !!geometry.docked && this.props.visible && !geometry.maximized;
        const key = inSidebar ? String(this.props.sidebarWidth) : null;
        if (key === this.splitKey) {
            return;
        }
        this.splitKey = key;
        if (inSidebar) {
            this.props.setSplitScreen(this.id, "left", this.props.sidebarWidth, false);
        } else {
            this.props.setSplitScreen(this.id, null, null, false);
        }
    };
    highlight = (item) => {
        if (!item) {
            return;
        }
        this.props.removeLayer("identifyslection");
        const layer = {
            id: "identifyslection",
            role: LayerRole.SELECTION
        };
        this.props.addLayerFeatures(layer, [item], true);
    };
    onClose = (ev) => {
        this.props.onClose();
        ev.stopPropagation();
    };
    getMaximized() {
        const sidewindows = this.props.sidewindows;
        if (sidewindows.length === 0) {
            return null;
        }
        if (this.props.sidebarMaximized === null || !sidewindows.some(item => item.id === this.props.sidebarMaximized)) {
            return sidewindows[0].id;
        }
        return this.props.sidebarMaximized;
    }
    getDynamicGeometry = () => {
        const maxSpace = window.innerHeight - 85;
        const windows = this.props.sidewindows.length;
        const openSize = maxSpace - ((windows - 1) * (MINIMIZED_HEIGHT + GAP));

        const geo = {...this.state.geometry};
        if (geo.docked) {
            geo.width = this.props.sidebarWidth;
            geo.x = 0;
            const maximizedId = this.getMaximized();
            if (maximizedId === this.id) {
                geo.minimized = false;
                geo.height = openSize;
            } else {
                geo.minimized = true;
                geo.height = MINIMIZED_HEIGHT;
            }
            const posi = this.props.sidewindows.findIndex(item => item.id === this.id);
            const posiMax = this.props.sidewindows.findIndex(item => item.id === maximizedId);
            geo.y = GAP + (Math.max(posi, 0) * (MINIMIZED_HEIGHT + GAP));
            if (posiMax >= 0 && posiMax < posi) {
                geo.y += (openSize - MINIMIZED_HEIGHT + GAP);
            }
        }
        return geo;
    };
    renderTitleBar = (geo) => {
        const maximized = this.state.geometry.maximized ? true : false;
        const minimized = geo.minimized ? true : false;
        const docked = this.state.geometry.docked;
        const iconClasses = classnames({
            "resizeable-window-titlebar-control": true,
            "resizeable-window-nodrag": true
        });
        let icon = null;
        if (this.props.icon) {
            icon = (<Icon className="resizeable-window-titlebar-icon" icon={this.props.icon} size="large" />);
        }
        return (
            <div className="resizeable-window-titlebar" onDoubleClick={this.toggleMaximize}>
                {icon}
                <span className="resizeable-window-titlebar-title" onClick={this.selectWindow}>
                    {this.props.title ? LocaleUtils.tr(this.props.title) : (this.props.titlelabel || "")}
                </span>
                {(this.props.extraControls || []).map(entry => {
                    const classes = classnames({
                        "resizeable-window-titlebar-extra-control": true,
                        "resizeable-window-titlebar-control-disabled": entry.disabled,
                        "resizeable-window-nodrag": true
                    });
                    const title = entry.title || (entry.titlemsgid ? LocaleUtils.tr(entry.titlemsgid) : undefined);
                    return (
                        <Icon
                            className={classes} icon={entry.icon} key={entry.icon}
                            onClick={entry.disabled ? undefined : entry.callback} title={title} />
                    );
                })}
                {!maximized && this.isDockable() ? (
                    <Icon
                        className={iconClasses} icon={docked ? "undock_left" : "dock_left"}
                        onClick={this.toggleDock}
                        title={docked ? LocaleUtils.tr("window.undock") : LocaleUtils.tr("window.dock")} />
                ) : null}
                {!docked && this.props.maximizeable ? (
                    <Icon className={iconClasses} icon={minimized ? "unminimize" : "minimize"}
                        onClick={this.toggleMinimize}
                        title={minimized ? LocaleUtils.tr("window.unminimize") : LocaleUtils.tr("window.minimize")} />
                ) : null}
                {this.props.maximizeable ? (
                    <Icon className={iconClasses} icon={maximized ? "unmaximize" : "maximize"}
                        onClick={this.toggleMaximize}
                        title={maximized ? LocaleUtils.tr("window.unmaximize") : LocaleUtils.tr("window.maximize")} />
                ) : null}
                {this.props.onClose ? (
                    <Icon className={iconClasses} icon="remove" onClick={this.onClose} title={LocaleUtils.tr("common.close")} />
                ) : null}
            </div>
        );
    };
    render() {
        const geo = this.getDynamicGeometry();
        const maximized = this.state.geometry.maximized ? true : false;
        const minimized = geo.minimized ? true : false;
        const docked = !!this.state.geometry.docked;
        const zIndex = this.props.baseZIndex + this.props.windowStacking.findIndex(item => item === this.id);

        const containerStyle = {
            left: (this.props.menuMargins?.left || 0) + 'px',
            right: (this.props.menuMargins?.right || 0) + 'px',
            top: this.props.topbarHeight + 'px',
            bottom: this.props.bottombarHeight + 'px',
            zIndex: zIndex,
            display: this.props.visible ? undefined : 'none'
        };
        const bodyclasses = classnames({
            "resizeable-window-body": true,
            "resizeable-window-body-scrollable": this.props.scrollable,
            "resizeable-window-body-nonscrollable": !this.props.scrollable,
            "resizeable-window-nodrag": true
        });
        const windowclasses = classnames({
            "resizeable-window": true,
            "resizeable-window-maximized": maximized,
            "resizeable-window-minimized": minimized,
            "customresizeable-window-docked": docked && !maximized,
            "customresizeable-window-docked-minimized": docked && minimized && !maximized
        });
        let resizeMode = {
            left: true, right: true, top: true, bottom: true,
            bottomLeft: true, bottomRight: true, topLeft: true, topRight: true
        };
        if (maximized || minimized) {
            resizeMode = false;
        } else if (docked) {
            // Only the sidebar width can be adjusted
            resizeMode = {right: true};
        }
        return (
            <div className="resizeable-window-container customresizeable-window-container" style={containerStyle}>
                <Rnd bounds="parent" cancel=".resizeable-window-nodrag"
                    className={windowclasses} default={geo}
                    disableDragging={maximized || docked}
                    enableResizing={resizeMode}
                    maxHeight={this.props.maxHeight || "100%"}
                    maxWidth={this.props.maxWidth || "100%"}
                    minHeight={this.props.minHeight} minWidth={this.props.minWidth}
                    onDragStart={this.onDragStart}
                    onDragStop={this.onDragStop}
                    onMouseDown={this.raiseWindow}
                    onResizeStop={this.onResizeStop}
                    position={{x: geo.x, y: geo.y}}
                    ref={this.initRnd}
                    size={{width: geo.width, height: geo.height}}>
                    <div className="resizeable-window-contents" onFocus={this.raiseWindow}>
                        {this.renderTitleBar(geo)}
                        <div className={bodyclasses} onMouseDown={this.raiseWindow}>
                            <div className="resizeable-window-drag-shield" ref={this.setDragShield} />
                            {this.props.children}
                        </div>
                    </div>
                </Rnd>
            </div>
        );
    }
    initRnd = (el) => {
        this.rnd = el;
    };
    setDragShield = (el) => {
        this.dragShield = el;
    };
    selectWindow = () => {
        if (this.state.geometry.docked) {
            this.highlight(this.props.feature);
            this.props.sidebarMaximize(this.id);
        }
    };
    raiseWindow = () => {
        const stacking = this.props.windowStacking;
        if (stacking[stacking.length - 1] !== this.id) {
            this.props.raiseWindow(this.id);
            this.highlight(this.props.feature);
        }
    };
    onDragStart = () => {
        if (this.dragShield) {
            this.dragShield.style.display = 'initial';
        }
    };
    onDragStop = (ev, data) => {
        this.setState((state) => ({geometry: {...state.geometry, x: data.x, y: data.y}}));
        if (this.dragShield) {
            this.dragShield.style.display = 'none';
        }
    };
    onResizeStop = (ev, dir, ref, delta, position) => {
        if (this.state.geometry.docked) {
            this.props.setSidebarWidth(this.props.sidebarWidth + delta.width);
        } else {
            this.setState((state) => ({
                geometry: {
                    ...state.geometry,
                    x: position.x,
                    y: position.y,
                    width: state.geometry.width + delta.width,
                    height: state.geometry.height + delta.height
                }
            }));
        }
    };
    toggleDock = () => {
        if (!this.state.geometry.docked) {
            this.props.registerSideWindow(this.id);
            this.props.sidebarMaximize(this.id);
        } else {
            this.props.unregisterSideWindow(this.id);
        }
        this.setState((state) => ({geometry: {...state.geometry, docked: !state.geometry.docked}}));
    };
    toggleMinimize = () => {
        this.highlight(this.props.feature);
        if (this.state.geometry.docked) {
            this.props.sidebarMaximize(this.getMaximized() === this.id ? null : this.id);
        } else {
            this.setState((state) => ({geometry: {...state.geometry, minimized: !state.geometry.minimized}}));
        }
    };
    toggleMaximize = () => {
        this.setState((state) => ({
            geometry: {...state.geometry, maximized: !state.geometry.maximized, minimized: false}
        }));
    };
}

export default connect((state) => ({
    windowStacking: state.windows.stacking,
    topbarHeight: state.windows.topbarHeight,
    bottombarHeight: state.windows.bottombarHeight,
    menuMargins: state.windows.menuMargins,
    sidewindows: state.sidebar.sidewindows,
    sidebarWidth: state.sidebar.width,
    sidebarMaximized: state.sidebar.maximized
}), {
    addLayerFeatures: addLayerFeatures,
    removeLayer: removeLayer,
    raiseWindow: raiseWindow,
    registerWindow: registerWindow,
    unregisterWindow: unregisterWindow,
    setSplitScreen: setSplitScreen,
    registerSideWindow: registerSideWindow,
    setSidebarWidth: setSidebarWidth,
    unregisterSideWindow: unregisterSideWindow,
    sidebarMaximize: sidebarMaximize
})(CustomResizeableWindow);
